import React, { useEffect, useState } from 'react';
import { Button, Modal, Spin, Typography } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import assistant from '../../services/assistant';
import { documentPreviewKind } from '../../utils/teamAssistantDocumentPreview';

const { Text, Paragraph } = Typography;

const TeamAssistantDocumentPreviewModal = ({ document: doc, open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [error, setError] = useState(null);

  const previewKind = doc ? documentPreviewKind(doc) : null;

  useEffect(() => {
    if (!open || !doc) {
      return undefined;
    }

    let revoked = false;
    let url = null;

    const load = async () => {
      setLoading(true);
      setError(null);
      setTextContent('');
      setBlobUrl(null);

      try {
        const blob = await assistant.fetchDocumentFile(doc.id, { inline: true });
        url = URL.createObjectURL(blob);

        if (revoked) {
          URL.revokeObjectURL(url);
          return;
        }

        if (documentPreviewKind(doc) === 'text') {
          const text = await blob.text();
          setTextContent(text.slice(0, 500_000));
        }

        setBlobUrl(url);
      } catch (e) {
        setError(e?.message || 'Could not load file');
      } finally {
        setLoading(false);
      }
    };

    load();

    return () => {
      revoked = true;
      if (url) URL.revokeObjectURL(url);
      setBlobUrl(null);
    };
  }, [open, doc?.id]);

  const handleDownload = async () => {
    if (!doc) return;
    try {
      const blob = await assistant.fetchDocumentFile(doc.id, { inline: false });
      const url = URL.createObjectURL(blob);
      const a = window.document.createElement('a');
      a.href = url;
      a.download = doc.original_name || 'download';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.message || 'Download failed');
    }
  };

  const zIndex = 1500;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={doc?.original_name || 'File preview'}
      width={previewKind === 'pdf' ? 900 : 720}
      zIndex={zIndex}
      wrapClassName="team-assistant-doc-preview-wrap"
      className="team-assistant-doc-preview-modal"
      footer={[
        <Button key="download" type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
          Download
        </Button>,
        <Button key="close" onClick={onClose}>Close</Button>,
      ]}
      destroyOnClose
    >
      {loading && (
        <div className="team-assistant-doc-preview-loading">
          <Spin />
        </div>
      )}
      {!loading && error && (
        <Paragraph type="danger">{error}</Paragraph>
      )}
      {!loading && !error && previewKind === 'image' && blobUrl && (
        <img
          src={blobUrl}
          alt={doc.original_name}
          className="team-assistant-doc-preview-image"
        />
      )}
      {!loading && !error && previewKind === 'pdf' && blobUrl && (
        <iframe
          title={doc.original_name}
          src={blobUrl}
          className="team-assistant-doc-preview-pdf"
        />
      )}
      {!loading && !error && previewKind === 'text' && (
        <pre className="team-assistant-doc-preview-text">{textContent}</pre>
      )}
      {!loading && !error && !previewKind && blobUrl && (
        <div className="team-assistant-doc-preview-fallback">
          <Text type="secondary">
            Preview is not available for this file type. Use Download to open it on your computer.
          </Text>
        </div>
      )}
    </Modal>
  );
};

export default TeamAssistantDocumentPreviewModal;
